import React from "react";
import {
  BarChart3,
  Building2,
  CheckCircle2,
  Copy,
  ExternalLink,
  LogOut,
  Mail,
  ShieldCheck,
  User as UserIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { Button } from "@/components/ui/button.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.jsx";
import { toast } from "sonner";
import ScoringPortalsWidget from "@/components/hotel/ScoringPortalsWidget.jsx";
import { resolveScoringAppUrl } from "@/utils/apiBaseUrl.js";

export default function UserProfileModal({ open, onOpenChange }) {
  const {
    userFirstName,
    email,
    userRole,
    roles,
    userId,
    profileUrl,
    userDetails,
    organizationName,
    organizationCode,
    organizationType,
    permissions,
    accessToken,
    hasScoringAccess,
    logout,
  } = useAuth();

  const handleGoToScoringApp = () => {
    const targetUrl = resolveScoringAppUrl(accessToken || "");
    window.open(targetUrl, "_blank");
  };

  const getInitials = (nameStr) => {
    if (!nameStr) return "U";
    const parts = nameStr.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return nameStr.slice(0, 2).toUpperCase();
  };

  const displayName =
    userDetails?.fullName ||
    userDetails?.name ||
    [userDetails?.firstName, userDetails?.lastName].filter(Boolean).join(" ") ||
    userFirstName ||
    (email ? email.split("@")[0] : "Staff Member");

  const avatarSrc = profileUrl || userDetails?.imageUrl || "";
  const roleTitle = userRole || roles?.[0] || "Hotel Staff";
  const permissionsCount = Array.isArray(permissions) ? permissions.length : 0;

  const handleCopyId = () => {
    if (userId) {
      navigator.clipboard.writeText(userId);
      toast.success("User ID copied to clipboard");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl p-6 border-slate-200/80 shadow-2xl">
        <DialogHeader className="items-center text-center space-y-3 pb-2 border-b border-slate-100">
          <div className="relative">
            <Avatar className="h-20 w-20 border-4 border-white shadow-md ring-2 ring-primary/20">
              {avatarSrc ? (
                <AvatarImage src={avatarSrc} alt={displayName} className="object-cover" />
              ) : null}
              <AvatarFallback className="bg-gradient-to-br from-primary via-rose-600 to-amber-600 text-white text-xl font-bold">
                {getInitials(displayName)}
              </AvatarFallback>
            </Avatar>
            <span
              className="absolute bottom-1 right-1 h-4 w-4 rounded-full bg-emerald-500 ring-2 ring-white"
              title="Active session"
            />
          </div>

          <div>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {displayName}
            </DialogTitle>
            <DialogDescription className="text-sm font-medium text-slate-500 mt-0.5">
              {email || "Logged in User"}
            </DialogDescription>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
            <Badge
              variant="outline"
              className="bg-primary/10 text-primary border-primary/20 font-semibold px-3 py-1 text-xs rounded-full"
            >
              <ShieldCheck className="mr-1 h-3.5 w-3.5" />
              {roleTitle}
            </Badge>

            <Badge
              variant="outline"
              className="bg-emerald-50 text-emerald-700 border-emerald-200/60 font-medium px-2.5 py-0.5 text-xs rounded-full"
            >
              <CheckCircle2 className="mr-1 h-3 w-3 text-emerald-500" />
              Active Session
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-3 text-sm">
          <div className="rounded-2xl bg-slate-50 p-3.5 border border-slate-100 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-400" /> Email
              </span>
              <span className="font-semibold text-slate-900 truncate max-w-[200px]">
                {email || "N/A"}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-500 font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400" /> Organization
              </span>
              <span className="font-semibold text-slate-900 truncate max-w-[200px]">
                {organizationName || "N/A"}
                {organizationCode ? ` (${organizationCode})` : ""}
              </span>
            </div>

            {organizationType && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500 font-medium flex items-center gap-2">
                  <UserIcon className="h-4 w-4 text-slate-400" /> Property Type
                </span>
                <span className="font-semibold text-slate-900">
                  {organizationType}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-slate-500 font-medium flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-slate-400" /> Access Privileges
              </span>
              <span className="font-semibold text-slate-900">
                {permissionsCount > 0 ? `${permissionsCount} permissions` : "Standard Access"}
              </span>
            </div>

            {userId && (
              <div className="flex items-center justify-between pt-1 border-t border-slate-200/60">
                <span className="text-slate-400 text-xs font-mono">
                  ID: {userId.slice(0, 8)}...{userId.slice(-4)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyId}
                  className="h-6 px-2 text-xs text-slate-500 hover:text-slate-900"
                >
                  <Copy className="mr-1 h-3 w-3" /> Copy ID
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
          {hasScoringAccess && (
            <ScoringPortalsWidget onLinkClick={() => onOpenChange(false)} />
          )}

          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onOpenChange(false);
              logout();
            }}
            className="w-full justify-center rounded-xl bg-red-600 hover:bg-red-700 font-semibold h-11"
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign Out
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
